import React from "react";
import { Link } from "react-router-dom";

const HomeAboutSection = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

        .about-root {
          font-family: 'DM Sans', sans-serif;
          background: #fafaf8;
          border-bottom: 1px solid #e8e8e4;
        }

        .about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
        }

        /* ── Left text pane ── */
        .about-text {
          padding: 8rem 7vw 8rem 7vw;
          border-right: 1px solid #e8e8e4;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .about-eyebrow {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        .about-eyebrow-line {
          width: 28px;
          height: 1px;
          background: #E78730;
          flex-shrink: 0;
        }

        .about-eyebrow-text {
          font-size: 0.68rem;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          color: #E78730;
          font-weight: 500;
        }

        .about-h2 {
          font-family: 'DM Serif Display', serif;
          font-size: clamp(2rem, 3.5vw, 3rem);
          line-height: 1.08;
          color: #1a1a1a;
          margin-bottom: 2.5rem;
          max-width: 18ch;
        }

        .about-h2 em {
          color: #E78730;
          font-style: italic;
        }

        .about-prose {
          margin-bottom: 3rem;
        }

        .about-prose p {
          font-size: 0.93rem;
          line-height: 1.9;
          color: #666;
          font-weight: 300;
          margin-bottom: 1.25rem;
        }
        .about-prose p:last-child { margin-bottom: 0; }

        .about-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.72rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #1a1a1a;
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid #E78730;
          padding-bottom: 2px;
          transition: color 0.2s, gap 0.2s;
        }
        .about-link:hover {
          color: #E78730;
          gap: 0.85rem;
        }

        .about-link svg {
          transition: transform 0.25s;
        }
        .about-link:hover svg {
          transform: translateX(3px);
        }

        /* ── Right image pane ── */
        .about-image-pane {
          background: #f0ede8;
          position: relative;
          overflow: hidden;
          min-height: 560px;
        }

        .about-image-pane img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          filter: grayscale(15%);
          transition: filter 0.5s, transform 0.5s;
        }

        .about-image-pane:hover img {
          filter: grayscale(0%);
          transform: scale(1.02);
        }

        /* Placeholder when no image */
        .about-img-placeholder {
          width: 100%;
          height: 100%;
          min-height: 560px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          background: #ede9e3;
        }

        .about-img-placeholder-icon {
          color: #c8c0b4;
        }

        .about-img-placeholder-text {
          font-size: 0.65rem;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: #c8c0b4;
        }

        /* Corner label on image */
        .about-image-label {
          position: absolute;
          bottom: 2rem;
          left: 2rem;
          background: rgba(10,10,10,0.7);
          backdrop-filter: blur(8px);
          padding: 0.6rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .about-image-label-dot {
          width: 6px;
          height: 6px;
          background: #E78730;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .about-image-label-text {
          font-size: 0.65rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.7);
          font-weight: 400;
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .about-grid {
            grid-template-columns: 1fr;
          }
          .about-text {
            border-right: none;
            border-bottom: 1px solid #e8e8e4;
            padding: 5rem 7vw;
          }
          .about-image-pane {
            min-height: 380px;
          }
          .about-img-placeholder {
            min-height: 380px;
          }
        }
      `}</style>

      <section className="about-root">
        <div className="about-grid">
          {/* ── Text ── */}
          <div className="about-text">
            <div className="about-eyebrow">
              <span className="about-eyebrow-line" />
              <span className="about-eyebrow-text">About Trademin</span>
            </div>

            <h2 className="about-h2">
              Your Trusted Partner in <em>Electrical &amp; Mechanical</em>{" "}
              Solutions
            </h2>

            <div className="about-prose">
              <p>
                At Trademin Ltd., we specialize in delivering exceptional
                electrical and mechanical services with an unwavering focus on
                quality, safety, and client satisfaction across the Canterbury
                region.
              </p>
              <p>
                Our team of highly qualified engineers and technicians brings
                decades of combined experience to every project, ensuring
                outcomes that consistently exceed industry standards and client
                expectations.
              </p>
            </div>

            <Link to="/about-us" className="about-link">
              Learn more about us
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </div>

          {/* ── Image ── */}
          <div className="about-image-pane">
            {/* Swap the empty string for your actual image src when ready */}
            {true ? (
              <div className="about-img-placeholder">
                <svg
                  className="about-img-placeholder-icon"
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                >
                  <rect x="3" y="3" width="18" height="18" rx="1" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                <span className="about-img-placeholder-text">
                  Image placeholder
                </span>
              </div>
            ) : (
              <img src="" alt="Professional engineering team collaboration" />
            )}

            <div className="about-image-label">
              <span className="about-image-label-dot" />
              <span className="about-image-label-text">
                Canterbury, New Zealand
              </span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomeAboutSection;
